import dateFormat from 'dateformat';
import { ApiClient, ApiError, ApiErrorCode } from '@bustickus/api';
import {
  ClientPassengerGender,
  IClientPassengerBrief,
  IClientPassengerFull,
  IClientPassengerPostPut,
  IClientPassengerPostPutForeignPassport,
  IClientPassengerPostPutPassport,
  IClientPassengerPostPutVisa,
} from '@bustickus/models-client';
import { createApiAddress } from 'common/helpers';
import { AuthService } from 'common/services';

class ApiPassengers {
  private readonly client: ApiClient = ApiClient.getInstance();
  private static instance: ApiPassengers | null = null;

  private constructor() {}

  static getInstance(): ApiPassengers {
    if (ApiPassengers.instance == null) {
      ApiPassengers.instance = new ApiPassengers();
    }

    return ApiPassengers.instance;
  }

  async getById(passengerId: string): Promise<IClientPassengerFull> {
    const jwt = AuthService.getInstance().get();
    const address = createApiAddress(`/client/passengers/${passengerId}`);
    const response = await this.client.sendRequest<IClientPassengerFull>('get', address, null, jwt);
    return response;
  }

  async getAll(
    needPassport?: boolean,
    needForeignPassport?: boolean,
    date?: Date,
    visaIds?: string[]
  ): Promise<IClientPassengerBrief[]> {
    const jwt = AuthService.getInstance().get();

    if (jwt == null) {
      throw new ApiError(ApiErrorCode.Unauthorized);
    }

    const request: any = {};

    if (needPassport != null) {
      request['needPassport'] = needPassport;
    }

    if (needForeignPassport != null) {
      request['needForeignPassport'] = needForeignPassport;
    }

    if (date != null) {
      request['date'] = dateFormat(date, 'yyyy-mm-dd');
    }

    if (visaIds != null) {
      request['visas'] = visaIds;
    }

    const address = createApiAddress('/client/passengers');
    const response = await this.client.sendRequest<IClientPassengerBrief[]>('get', address, request, jwt);
    return response;
  }

  async create(
    name: string,
    birthDate: Date,
    gender: ClientPassengerGender,
    passport: IClientPassengerPostPutPassport | null,
    foreignPassport: IClientPassengerPostPutForeignPassport | null,
    visas: IClientPassengerPostPutVisa[]
  ) {
    const jwt = AuthService.getInstance().get();

    if (jwt == null) {
      throw new ApiError(ApiErrorCode.Unauthorized);
    }

    const birthDateString = dateFormat(birthDate, 'yyyy-mm-dd');

    const address = createApiAddress('/client/passengers');
    const request: IClientPassengerPostPut = {
      name: name,
      gender: gender,
      birthDate: birthDateString,
      passport: passport,
      foreignPassport: foreignPassport,
      visas: visas,
    };

    await this.client.sendRequest('post', address, request, jwt);
  }

  async update(
    passengerId: string,
    name: string,
    birthDate: Date,
    gender: ClientPassengerGender,
    passport: IClientPassengerPostPutPassport | null,
    foreignPassport: IClientPassengerPostPutForeignPassport | null,
    visas: IClientPassengerPostPutVisa[]
  ) {
    const jwt = AuthService.getInstance().get();

    if (jwt == null) {
      throw new ApiError(ApiErrorCode.Unauthorized);
    }

    const birthDateString = dateFormat(birthDate, 'yyyy-mm-dd');

    const address = createApiAddress(`/client/passengers/${passengerId}`);
    const request: IClientPassengerPostPut = {
      name: name,
      gender: gender,
      birthDate: birthDateString,
      passport: passport,
      foreignPassport: foreignPassport,
      visas: visas,
    };

    await this.client.sendRequest('put', address, request, jwt);
  }

  async delete(passengerId: string) {
    const jwt = AuthService.getInstance().get();

    if (jwt == null) {
      throw new ApiError(ApiErrorCode.Unauthorized);
    }

    const address = createApiAddress(`/client/passengers/${passengerId}`);
    await this.client.sendRequest('delete', address, null, jwt);
  }
}

export default ApiPassengers;
